import * as React from "react";
import { Wizard } from "react-use-wizard";
import questionnaire from "../../models/OnboaringQuestionnaire";
import NameStep from "./nameStep";
import SingleChoiceStep from "./singleChoiceStep";
import { useEffect, useState } from "react";
import TextInputStep from "./textInputStep";
import GenderStep from "./genderStep";
import MobileStep from "./mobileStep";
import MobileVerifyStep from "./mobileVerifyStep";
import EmailStep from "./emailStep";
import AddressStep from "./addressStep";
import CongratulationsStep from "./congratulationStep";
import { useTranslation } from "react-i18next";
import DOBInputStep from "./dobStep";
import TermsConditionsStep from "./termsConditionStep";
import createPersistedState from "use-persisted-state";
import stepMap from "./utils";
import { OnboardingApi } from "./OnboardingApi";
import { useLocation, useHistory } from "react-router-dom";
import NavigateBack from "../../components/navigateBack";

const useOnboardingState = createPersistedState("onboardingState");
const useOnboardingStep = createPersistedState("onboardingStep");
const useOnboardingHistory = createPersistedState("onboardingHist");

function OnboardingWizard() {
  const qDetails = questionnaire;
  const [cachedState, setCachedState] = useOnboardingState();
  const [cachedStep, setCachedStep] = useOnboardingStep();
  const [wizardHistory, setWizardHistory] = useState([]);
  const [state, setState] = useState();
  const [step, setStep] = useState();
  const [renderWizard, setRenderWizard] = useState(false);
  const { t } = useTranslation();
  const [onboardingHistory, setOnboardingHistory] = useOnboardingHistory([]);
  const [settings, setSettings] = useState(undefined);
  const [isApiError, setIsApiError] = useState(false);
  const [isApiLoading, setIsApiLoading] = useState(false);

  const location = useLocation();
  const history = useHistory();

  const queryParams = new URLSearchParams(location.search);
  const [clinicIdFromQrCode, setClinicIdFromQrCode] = useState(
    queryParams.get("cid")?.toLocaleUpperCase() || undefined
  );

  useEffect(() => {
    setWizardHistory(onboardingHistory);
  }, [onboardingHistory]);

  useEffect(() => {
    cachedState ? setState(cachedState) : setState({});
  }, [cachedState]);

  useEffect(() => {
    cachedStep ? setStep(cachedStep) : setStep(0);
  }, [cachedStep]);

  // useEffect(() => {
  //   setRenderWizard(true);
  // }, [state, step]);

  useEffect(() => {
    const fetchSettings = async () => {
      setIsApiLoading(true);
      try {
        if (settings === undefined) {
          setRenderWizard(false);
          const response = await OnboardingApi.getSettings();
          if (response) {
            setRenderWizard(true);
            setSettings(response);
          } else {
            setIsApiError(true);
          }
        } else {
          setRenderWizard(true);
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
        setIsApiError(true);
      } finally {
        setIsApiLoading(false);
      }
    };

    fetchSettings();
  }, []);

  useEffect(() => {
    if (clinicIdFromQrCode) {
      setState((state) => {
        return {
          ...state,
          KNOWS_THE_DOCTORS:
            questionnaire.questions.KNOWS_THE_DOCTORS.choices[0],
          "PREF-TREATING-LOC":
            questionnaire.questions["PREF-TREATING-LOC"].choices.find(
              (clinic) => clinic.value === clinicIdFromQrCode
            ) || "CC000001",
        };
      });
    }
  }, []);

  const getDoctors = () => {
    if (settings === undefined) {
      return translateChoices(
        qDetails.questions["CHOOSE-DOC"]["choices"],
        "onboard.CHOOSE_DOC.options."
      );
    }

    //filter out dietitians from doctors list
    const doctors = settings.doctors.filter(
      (doctor) => doctor.healthProviderType === "Doctor"
    );

    if (clinicIdFromQrCode) {
      return translateChoices(
        doctors
          .filter(
            (doctor) =>
              doctor.clinics.filter(
                (clinic) =>
                  clinic.code == getSubState("PREF-TREATING-LOC")?.value
              ).length > 0
          )
          .map((doctor) => {
            return {
              value: doctor.code,
              text: doctor.name,
              next: "PID",
            };
          }),
        "onboard.CHOOSE_DOC.options."
      ).map((doctor) => {
        return { ...doctor, next: "PID" };
      });
    }

    if (getSubState("KNOWS_THE_DOCTORS")?.value === "no") {
      return translateChoices(
        doctors
          .filter(
            (doctor) =>
              doctor.clinics.filter(
                (clinic) =>
                  clinic.code == getSubState("PREF-TREATING-LOC")?.value
              ).length > 0
          )
          .map((doctor) => {
            return {
              value: doctor.code,
              text: doctor.name,
              next: "DOB",
            };
          }),
        "onboard.CHOOSE_DOC.options."
      ).map((doctor) => {
        return { ...doctor, next: "DOB" };
      });
    }

    return translateChoices(
      doctors.map((doctor) => {
        return {
          value: doctor.code,
          text: doctor.name,
          next: "PID",
        };
      }),
      "onboard.CHOOSE_DOC.options."
    ).map((doctor) => {
      return { ...doctor, next: "PID" };
    });
  };

  const getClinicsForDoctor = (docId, nextStep) => {
    if (settings && settings.hasOwnProperty("doctors")) {
      return translateChoices(
        settings.doctors
          .filter((doctor) => {
            return doctor.code === docId;
          })
          .flatMap((doctor) => {
            return doctor.clinics;
          })
          .map((clinic) => {
            return {
              value: clinic.code,
              text: clinic.name,
              disabled: false,
              hasOldPatients: true,
            };
          }),
        "onboard.PREF_TREATING_LOC.options."
      ).map((clinic) => {
        return { ...clinic, next: nextStep };
      });
    } else {
      return [];
    }
  };

  const getIC4CLinics = (nextStep) => {
    return translateChoices(
      qDetails.questions["PREF-TREATING-LOC"]["choices"],
      "onboard.PREF_TREATING_LOC.options."
    )
      .map((clinic) => {
        return {
          value: clinic.value,
          text: clinic.text,
          disabled: false,
          hasOldPatients: true,
          next: nextStep,
        };
      })
      .filter((clinic) => clinic.value != "CC000000");
  };

  const getClinics = (nextStep) => {
    let docId = undefined;
    if (getSubState("KNOWS_THE_DOCTORS")?.value === "no") {
      console.log("clinics", getIC4CLinics("CHOOSE-DOC"));
      return getIC4CLinics("TREATING-DOC");
    }

    if (
      getSubState("TREATING-DOC") &&
      getSubState("TREATING-DOC").hasOwnProperty("value")
    ) {
      docId = getSubState("TREATING-DOC")["value"];
    }

    if (
      (settings !== undefined && docId !== undefined && docId !== "DR000001") ||
      (settings !== undefined &&
        docId !== undefined &&
        docId === "DR000001" &&
        nextStep === "DOB")
    ) {
      return getClinicsForDoctor(docId, nextStep);
    } else {
      return translateChoices(
        qDetails.questions["TREATING-LOC"]["choices"],
        "onboard.TREATING_LOC.options."
      );
    }
  };

  const updateStep = (deltaState, updateHistory = false) => {
    const steps = Object.keys(deltaState);
    console.log("steps", steps);
    if (steps && steps.length > 0) {
      const currentStep = stepMap[steps[0]];
      setCachedStep(currentStep);
      if (updateHistory) {
        let newHistory = wizardHistory;
        newHistory.push(currentStep);
        console.log(newHistory);
        setOnboardingHistory(newHistory);
      }
    }
  };

  const getPreviousStep = () => {
    console.log(wizardHistory);
    let previousStep = undefined;
    if (wizardHistory.length > 0) {
      previousStep = wizardHistory[wizardHistory.length - 1];
      wizardHistory.pop();
      setOnboardingHistory(wizardHistory);
      console.log("-----------");
      console.log(previousStep);
      setCachedStep(previousStep);
      return previousStep;
    } else {
      return -1;
    }
  };

  const updateStatus = (deltaState, updateHistory = false) => {
    console.log(deltaState);
    console.log(state);
    updateStep(deltaState, updateHistory);
    setState({
      ...state,
      ...deltaState,
    });
    setCachedState({
      ...state,
      ...deltaState,
    });
  };

  /*
  const isIC4ID = (patientId) => parsePID(patientId).match(/^\d{8}$/) !== null;
  const fetchPIDURL = (patientId) => `/api/v1/users/register/recover/${patientId}`;
  const fetchUserDetails = (deltaState) => {
    if(isIC4ID(deltaState['PID'])) {
      fetch(fetchPIDURL(deltaState['PID']), {
        method: "GET"
      })
          .then((response) => response.json())
          .then((responseData) => {
            console.log(responseData);
            if (responseData["status"]["success"]) {
              updateStatus({
                "MOBILE": responseData['mobile'],
                "EMAIL": {
                  "email": responseData['email'],
                  "confirmEmail": responseData['email']
                },
                ...deltaState
              });
            }
          });
    } else {
      updateStatus(deltaState);
    }
  }

 */

  const getSubState = (qKey) => {
    return state.hasOwnProperty(qKey) ? state[qKey] : null;
  };

  const translateChoices = (choices, translationPrefix) => {
    return choices.map((choice) => {
      return {
        ...choice,
        text: t(translationPrefix + choice.value),
      };
    });
  };

  return !renderWizard ? (
    <>
      <NavigateBack />
      <div
        style={{
          margin: "auto",
          marginTop: "6em",
          display: "flex",
          justifyContent: "center",
        }}
      >
        {isApiError ? (
          <span style={{ color: "red" }}>
            Something went wrong. Please try again.
          </span>
        ) : isApiLoading ? (
          <div id="loader" className="loader"></div>
        ) : null}
      </div>
    </>
  ) : (
    <>
      <Wizard startIndex={cachedStep ? cachedStep : 0}>
        <NameStep
          question={t("onboard.NAME.question")}
          qKey={"NAME"}
          state={getSubState("NAME")}
          onSubmitHandler={updateStatus}
          isOpenedFromClinicQrCode={clinicIdFromQrCode != undefined}
        />
        {/*
          <SingleChoiceStep
            question={t("onboard.TREATING_DOC.question")}
            choices={translateChoices(
              qDetails.questions["TREATING-DOC"]["choices"],
              "onboard.TREATING_DOC.options."
            )}
            qKey={"TREATING-DOC"}
            state={getSubState("TREATING-DOC")}
            onSubmitHandler={updateStatus}
          />
          */}
        <SingleChoiceStep
          question={t("onboard.KNOWS_THE_DOCTORS.question")}
          choices={translateChoices(
            qDetails.questions["KNOWS_THE_DOCTORS"]["choices"],
            "onboard.KNOWS_THE_DOCTORS.options."
          )}
          qKey={"KNOWS_THE_DOCTORS"}
          state={getSubState("KNOWS_THE_DOCTORS")}
          onSubmitHandler={updateStatus}
          prevWizardStep={getPreviousStep}
        />
        <SingleChoiceStep
          question={t("onboard.CHOOSE_DOC.question")}
          choices={getDoctors()}
          qKey={"TREATING-DOC"}
          state={getSubState("TREATING-DOC")}
          onSubmitHandler={updateStatus}
          prevWizardStep={getPreviousStep}
        />
        <SingleChoiceStep
          question={(() => {
            const question = t("onboard.EXISTING_PATIENT.question");
            if (
              getSubState("TREATING-DOC") &&
              getSubState("TREATING-DOC").hasOwnProperty("text")
            ) {
              return question.replace(
                "<Doctor Name>",
                getSubState("TREATING-DOC")["text"]
              );
            } else {
              return question;
            }
          })()}
          choices={translateChoices(
            qDetails.questions["EXISTING-PATIENT"]["choices"],
            "onboard.EXISTING_PATIENT.options."
          )}
          qKey={"EXISTING-PATIENT"}
          state={getSubState("EXISTING-PATIENT")}
          onSubmitHandler={updateStatus}
          prevWizardStep={getPreviousStep}
        />
        <SingleChoiceStep
          question={t("onboard.TREATING_LOC.question")}
          choices={getClinics("PID")}
          qKey={"TREATING-LOC"}
          state={getSubState("TREATING-LOC")}
          onSubmitHandler={updateStatus}
          prevWizardStep={getPreviousStep}
        />
        <SingleChoiceStep
          question={t("onboard.PREF_TREATING_LOC.question")}
          choices={getIC4CLinics("PID")}
          qKey={"PREF-TREATING-LOC"}
          state={getSubState("PREF-TREATING-LOC")}
          onSubmitHandler={updateStatus}
          prevWizardStep={getPreviousStep}
        />
        <TextInputStep
          question={t("onboard.PID.question")}
          descriptions={[
            t("onboard.PID.description1"),
            t("onboard.PID.description2"),
          ]}
          qKey={"PID"}
          state={getSubState("PID")}
          labelText1={t("onboard.PID.inputLbl")}
          labelText2={t("onboard.PID.inputLbl")}
          subHeaderText={t("onboard.PID.inputLbl1")}
          onSubmitHandler={updateStatus}
          prevWizardStep={getPreviousStep}
          isOpenedFromClinicQrCode={clinicIdFromQrCode != undefined}
        />
        <DOBInputStep
          userName={getSubState("NAME") ? getSubState("NAME")["first"] : ""}
          qKey={"DOB"}
          state={getSubState("DOB")}
          onSubmitHandler={updateStatus}
          prevWizardStep={getPreviousStep}
        />
        <GenderStep
          question={t("onboard.GENDER.question")}
          qKey={"GENDER"}
          state={getSubState("GENDER")}
          onSubmitHandler={updateStatus}
        />
        <MobileStep
          question={t("onboard.MOBILE.question")}
          qKey={"MOBILE"}
          state={getSubState("MOBILE")}
          labelText={t("onboard.MOBILE.inputLbl")}
          patientId={getSubState("PID")}
          onSubmitHandler={updateStatus}
        />
        <MobileVerifyStep
          question={t("onboard.VERIFY_MOBILE.question")}
          qKey={"MOBILE-VERIFY"}
          otpNumber={getSubState("MOBILE")}
          patientId={getSubState("PID")}
          onSubmitHandler={updateStatus}
        />
        <EmailStep
          qKey={"EMAIL"}
          state={getSubState("EMAIL")}
          onSubmitHandler={updateStatus}
        />
        <AddressStep
          qKey={"RESIDENCE"}
          state={getSubState("RESIDENCE")}
          onSubmitHandler={updateStatus}
        />
        <TermsConditionsStep
          qKey={"TERMS"}
          state={state}
          onSubmitHandler={updateStatus}
        />
        <CongratulationsStep
          qKey={"CONGRATS"}
          profile={state}
          onSubmitHandler={updateStatus}
        />
      </Wizard>
    </>
  );
}

export default OnboardingWizard;
